// Main Layout Component

import { useState, useEffect, useCallback } from 'react';
import { useMapStore, useUserStore, useSearchStore } from '../../stores';
import { MapContainer } from '../map/MapContainer';
import { MapControls } from '../map/MapControls';
import { LeadSidebar } from '../leads/LeadSidebar';
import { LeadDetailPanel } from '../leads/LeadDetailPanel';
import { Header } from './Header';
import { SearchOverlay } from '../map/SearchOverlay';
import { DirectionsPanel } from '../map/DirectionsPanel';
import { WhereAmIPanel } from '../map/WhereAmIPanel';
import { useNavStore } from '../../stores/navStore';
import { api } from '../../lib/api';
import { getCurrentPosition } from '../../lib/geolocation';
import { MAP_STYLES } from '../map/MapContainer';
import { cn } from '../../lib/utils';
import { Loader2, MapPin, Search, Filter, X, Map as MapIcon } from 'lucide-react';

export function MainLayout() {
  const {
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    controlsOpen,
    setControlsOpen,
    toggleControls,
    rightPanelOpen,
    setRightPanelOpen,
    rightPanelTab,
    setRightPanelTab,
    selectedLead,
    setSelectedLead,
    leads,
    setLeads,
    addLeads,
    isLoadingLeads,
    setLoadingLeads,
    mapState,
    setMapState,
    viewMode,
    setViewMode,
    filters,
    setFilters,
  } = useMapStore();

  const { user } = useUserStore();
  const { searchQuery, setSearchQuery, addRecentLocation } = useSearchStore();
  const { panel, setPanel, whereAmI, setWhereAmI, setLocationError } = useNavStore();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);

  // Load initial leads on mount and when map moves significantly
  useEffect(() => {
    const loadLeads = async () => {
      setLoadingLeads(true);
      try {
        const newLeads = await api.fetchLeads(mapState.center, filters.radius / 1000, 200);
        setLeads(newLeads);
      } catch (error) {
        console.error('Failed to load leads:', error);
      } finally {
        setLoadingLeads(false);
      }
    };

    loadLeads();
  }, [mapState.center.lat, mapState.center.lng, mapState.zoom, filters.radius]);

  const handleLeadClick = (lead: any) => {
    setSelectedLead(lead);
    setRightPanelOpen(true);
    setRightPanelTab('details');
    addRecentLocation(lead.coordinates);
    
    // On mobile, close sidebar and open detail panel
    if (window.innerWidth < 1024) {
      setIsMobileSidebarOpen(false);
      setIsMobileDetailOpen(true);
    }
  };

  const handleMapMove = (center: any, zoom: number) => {
    setMapState({ center, zoom });
  };

  const handleSearch = useCallback(() => {
    setShowSearchOverlay(true);
  }, []);

  /**
   * Single entry point for the leads list, used by the header button.
   * On desktop it slides the docked sidebar in/out; below the lg breakpoint
   * the sidebar does not exist, so we open the overlay drawer instead.
   */
  const handleToggleLeads = useCallback(() => {
    if (window.innerWidth < 1024) {
      setIsMobileSidebarOpen((open) => !open);
    } else {
      toggleSidebar();
    }
  }, [toggleSidebar]);

  const handleFlyToCurrentLocation = useCallback(() => {
    // If the Where-am-I panel already holds a fix, just fly to it. Issuing a
    // second bare getCurrentPosition() here used to request a *low accuracy*
    // position (no enableHighAccuracy), so `H` could jump to a coarser spot
    // than the dot already on the map.
    if (whereAmI) {
      setMapState({ center: whereAmI.coords, zoom: 16 });
      addRecentLocation(whereAmI.coords);
      return;
    }

    getCurrentPosition({ highAccuracy: true, timeout: 12_000, maximumAge: 0 })
      .then((fix) => {
        setMapState({ center: fix.coords, zoom: 16 });
        addRecentLocation(fix.coords);
        setWhereAmI(fix);
      })
      .catch((err) => {
        console.error('Geolocation error:', err);
        // Surface it in the panel instead of silently doing nothing.
        setLocationError(err instanceof Error ? err.message : 'Could not get your location.');
        setPanel('location');
      });
  }, [whereAmI, setMapState, addRecentLocation, setWhereAmI, setLocationError, setPanel]);

  // Global keyboard shortcuts (Google Maps-like): Cmd+K search, view modes, sidebar
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping = target instanceof HTMLInputElement || 
                       target instanceof HTMLTextAreaElement || 
                       target.isContentEditable;

      // Cmd/Ctrl+K opens search (works even while typing)
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setShowSearchOverlay(true);
        return;
      }

      if (isTyping || showSearchOverlay) return;

      switch (event.key.toLowerCase()) {
        case 'm':
          event.preventDefault();
          setViewMode('map');
          break;
        case 'l':
          event.preventDefault();
          setViewMode('list');
          break;
        case '3':
          event.preventDefault();
          setViewMode('3d');
          break;
        case 's':
          event.preventDefault();
          setViewMode('street');
          break;
        case 'h':
          event.preventDefault();
          handleFlyToCurrentLocation();
          break;
        case 'g':
          event.preventDefault();
          setPanel('location');
          break;
        case 'd':
          event.preventDefault();
          setPanel('directions');
          break;
        case 'escape':
          setSelectedLead(null);
          setRightPanelOpen(false);
          setPanel('none');
          setControlsOpen(false);
          break;
        case '/':
          event.preventDefault();
          setShowSearchOverlay(true);
          break;
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [setViewMode, setSelectedLead, setRightPanelOpen, handleFlyToCurrentLocation, showSearchOverlay, setPanel, setControlsOpen]);

  // Determine style support
  const currentStyle = MAP_STYLES[mapState.style as keyof typeof MAP_STYLES] || MAP_STYLES.streets;
  const is3DSupported = currentStyle.type === 'vector';
  const isTerrainSupported = mapState.style === 'terrain';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <Header 
        onMenuClick={toggleControls}
        onSearchClick={handleSearch}
        onLeadsClick={handleToggleLeads}
      />

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        >
          <div className="absolute left-0 top-0 bottom-0 w-96 bg-white shadow-xl animate-slide-in-left">
            <LeadSidebar 
              onLeadClick={handleLeadClick}
              onClose={() => setIsMobileSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Mobile Detail Panel Overlay */}
      {isMobileDetailOpen && rightPanelOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 lg:hidden"
          onClick={() => setIsMobileDetailOpen(false)}
        >
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl animate-slide-in-right">
            <LeadDetailPanel
              lead={selectedLead}
              onClose={() => {
                setIsMobileDetailOpen(false);
                setRightPanelOpen(false);
              }}
              activeTab={rightPanelTab}
              onTabChange={setRightPanelTab}
            />
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {showSearchOverlay && (
        <SearchOverlay
          onClose={() => setShowSearchOverlay(false)}
          onSearch={(query) => {
            setSearchQuery(query);
            setShowSearchOverlay(false);
          }}
          recentLocations={[]}
        />
      )}

      {/* Main Content Area */}
      <div className="pt-16 h-[calc(100vh-4rem)] relative">
        {/* Map Container */}
        <div className="absolute inset-0">
          <MapContainer
            onLeadClick={handleLeadClick}
            onMapMove={handleMapMove}
          />
        </div>

        {/* Desktop Sidebar — docked below the header. It must be a real block box with
            an explicit width and vertical bounds, because LeadSidebar now
            fills it with `absolute inset-0` rather than fixing itself to the
            viewport (which used to cover the header). */}
        <aside className="hidden lg:block lg:fixed lg:left-0 lg:top-16 lg:bottom-0 lg:w-96 z-30">
          <LeadSidebar 
            onLeadClick={handleLeadClick}
          />
        </aside>

        {/* Navigation panel (directions / where-am-i).
            Inset from the right edge with rounded corners so it is never
            flush against the window edge or clipped by the header. */}
        {panel !== 'none' && (
          <aside
            className="fixed z-30 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl
                       bottom-3 right-3 top-[4.5rem]
                       w-[min(24rem,calc(100vw-1.5rem))]
                       animate-slide-in-right"
          >
            {panel === 'directions' ? <DirectionsPanel /> : <WhereAmIPanel />}
          </aside>
        )}

        {/* Desktop Detail Panel — same rule as the leads sidebar: a real positioned
            box below the header, with the panel filling it. */}
        <aside className={cn(
          'hidden lg:block lg:fixed lg:right-0 lg:top-16 lg:bottom-0 z-30 transition-transform duration-300',
          rightPanelOpen && panel === 'none' ? 'w-96 translate-x-0' : 'w-96 translate-x-full'
        )}>
          <LeadDetailPanel
            lead={selectedLead}
            onClose={() => setRightPanelOpen(false)}
            activeTab={rightPanelTab}
            onTabChange={setRightPanelTab}
          />
        </aside>

        {/* Map Controls - New Google Maps-like toolbar */}
        <MapControls
          onViewModeChange={setViewMode}
          viewMode={viewMode}
          onToggleHeatmap={() => {}}
          showHeatmap={false}
          onToggle3D={() => setMapState({ show3DBuildings: !mapState.show3DBuildings })}
          is3DEnabled={mapState.show3DBuildings}
          is3DSupported={is3DSupported}
          onToggleTerrain={() => setMapState({ style: mapState.style === 'terrain' ? 'streets' : 'terrain' })}
          isTerrainEnabled={mapState.style === 'terrain'}
          isTerrainSupported={isTerrainSupported}
        />

        {/* Floating Action Buttons - Mobile.
            The "Leads" button used to live here, but it is now a permanent
            top-right header button, so this corner only carries location. */}
        <div className="lg:hidden fixed bottom-6 right-4 z-30 flex flex-col gap-2">
          <button
            onClick={handleFlyToCurrentLocation}
            className="bg-white shadow-lg p-3 rounded-xl"
            aria-label="Go to my location"
          >
            <MapPin className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Lead Count Badge - Desktop */}
        <div className="hidden lg:fixed bottom-6 left-24 z-20 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-slate-200 flex items-center gap-2 animate-fade-in">
          <div className="w-2 h-2 rounded-full bg-primary-500"></div>
          <span className="text-sm font-medium text-slate-700">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} in view
          </span>
          {isLoadingLeads && (
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
          )}
        </div>

        {/* Search Bar - Mobile */}
        <div className="lg:hidden fixed bottom-6 left-4 right-16 z-20">
          <button
            onClick={handleSearch}
            className="w-full btn-secondary shadow-lg justify-start"
          >
            <Search className="w-5 h-5" />
            <span>Search...</span>
            <kbd className="ml-auto px-2 py-0.5 text-xs bg-slate-100 rounded">⌘K</kbd>
          </button>
        </div>
      </div>
    </div>
  );
}

export default MainLayout;